import { observable, action, computed, toJS } from 'mobx'
import { differenceBy, get, isEqual, isEmpty, isNil, maxBy, minBy, omitBy, orderBy, uniqBy, last } from 'lodash'
import {
  MESSAGE_TYPE_CONTACT_INFO,
  MESSAGE_TYPE_UPGRADE,
  MESSAGE_TYPE_DEFAULT,
  MESSAGE_TYPE_IMAGE,
  PENDING_MESSAGE_ID,
  MESSAGE_TYPE_SEXICON,
  MESSAGE_TYPE_SEXICONS_UPGRADE,
  MESSAGE_TYPE_FEATURED_AP_UNLOCK,
  MESSAGE_TYPE_VOICE,
} from 'core/constants'
import storage from 'core/storage'
import { generateNotSentId, wait } from 'core/helpers'
import * as actions from '../actions'
import { ccpProvider } from 'core/CcpProvider'

import Message from './Message'
import store from '../store'
import CONFIG from 'core/globals'
import UnlockVipSlice from '../unlockVip/unlock-vip.slice'

export default class Conversation {

  @observable.shallow messages = []

  @observable meReadAt = 0
  @observable meMessageCount = 0
  @observable.shallow meProfile = {}

  @observable lastMessageSentAt = 0
  @observable.shallow otherUserProfile = {}

  @observable unreadByMeCount = 0

  @observable typingIndicator = 0
  @observable messageReceivedAt = 0
  @observable notificationReceivedAt = 0

  @observable.shallow tempMessages = []

  @observable giftsBlock = false
  @observable wasTempUnblocked = undefined;
  @observable.shallow showVirtualHottie = undefined;

  @observable featuredApUnlock = undefined;
  @observable isBlocked = false;

  constructor(data) {
    const meIndex = CONFIG.userUid === data.user0Uid ? 0 : 1
    const otherIndex = meIndex ^ 1
    const getKey = (name, oldName) => Object.prototype.hasOwnProperty.call(data, `${name}`) ? name : oldName
    const me = key => data[getKey(`me${key}`, `user${meIndex}${key}`)]
    const otherUser = key => data[getKey(`otherUser${key}`, `user${otherIndex}${key}`)]

    this.meIndex = meIndex
    this.conversationUid = data.conversationUid
    this.meUid = me('Uid')
    this.meHasDeleted = me('HasDeleted')
    this.meReadAt = me('ReadAt')
    this.meProfile = me('Profile') || {} // in case server returned null (happens in DF)
    this.meMessageCount = me('MessageCount') || 0
    this.otherUserUid = otherUser('Uid')
    this.otherUserProfile = otherUser('Profile') || {} // in case server returned null (happens in DF)
    this.unreadByMeCount = data[getKey('unreadByMeCount', `unreadBy${meIndex}Count`)] || 0
    this.identifier = data.identifier // whitelabelUid
    this.lastMessageSentAt = data.lastMessageSentAt
    this.hasMoreMessages = true
    this.typingIndicator = 0
    this.messageReceivedAt = 0
    this.notificationReceivedAt = 0
    this.interfaceNotificationIndex = 0
    this.createdAt = data.createdAt || null
    this.error = data.error
    this.deletedAt = data.deletedAt
    this.restoredAt = data.restoredAt

    this.tempMessages = []

    this.giftsBlock = false

    if (data.messages) {
      this.messages.push(...data.messages.filter(m => !this.messages.find(e => e.id == m.id)).map(x => new Message(x)))
    }

    if (this.meMessageCount) {
      this.userInteracted()
    }

    this.setLocations()

    this.wasTempUnblocked = data.wasTempUnblocked ? data.wasTempUnblocked : undefined
    this.showVirtualHottie = data.showVirtualHottie ? data.showVirtualHottie : undefined

    this.featuredApUnlock = new UnlockVipSlice();
    this.featuredApUnlock.isChatLocked = this.otherUserProfile.isFeatured && !this.meProfile.isFeatured && !data.featuredUnlocked ? true : false;
  }

  @computed get oldestSentAt() {
    return get(minBy(this.messages, 'sentAt'), 'sentAt')
  }

  @computed get lastMessage() {
    return get(maxBy(this.messages, 'id'), 'message')
  }

  @computed get lastMessageOfOtherUser() {
    return get(maxBy(this.messages.filter( m => m.senderUid === this.otherUserUid && m.type === 'message'), 'id'), 'message')
  }

  @action
  async fetchMessages(lastSentAt = null) {
    const params = {
      lastSentAt,
      userUid: this.meUid,
      otherUid: this.otherUserUid,
    }

    //TODO decide to cache and replay events
    const [response, error] = await ccpProvider.api.ccp.fetchMessages(omitBy(params, isNil),)
    if (error) {
      if (store.isDev) {
        console.warn('error fetching messages: ', error)
      }
      return
    }
    const newMessages = differenceBy(response.messages, this.messages, 'id')

    if (newMessages.length) {
      this.unreadByMeCount = newMessages.length
      if (last(newMessages).senderUid === this.otherUserUid) {
        this.lastMessageSentAt = last(newMessages).sentAt
      }

      this.messages.push(...newMessages.map(x => new Message(x)))

    }
    actions.chat.updateConversationCache(this)

    // If no new messages, set flag
    if (lastSentAt && newMessages.length === 0) {
      this.hasMoreMessages = false
    }
  }

  @action
  async markAsDeleted() {
    // await request.post(endpoints.DELETE_CONVERSATION, { conversationUid: this.conversationUid })
    console.warn('markAsDeleted functionality is disabled due to missing BE support')
    store.conversations = store.conversations.filter(c => c.conversationUid !== this.conversationUid)
  }

  @action
  async send(message, specialType = false, isFree = false) {
    const { onMessageSend } = CONFIG.hooks
    const data = {
      senderUid: this.meUid,
      receiverUid: this.otherUserUid,
      message,
      type: specialType || Message.DEFAULT_TYPE,
      fromWidget: true,
      isFree: isFree,
    }
    if (CONFIG.siteName) {
      data.siteName = CONFIG.siteName
    }

    /*add directly a message to speed up the conversation
    and remove the last message if a contact gets blocked*/
    const newMessage = this.addMessage({
      message,
      id: PENDING_MESSAGE_ID,
      sentAt: parseInt(Date.now()/1000),
      sentBy: this.meIndex,
      type: specialType || Message.DEFAULT_TYPE,
    })
    const [response, error] = await ccpProvider.api.ccp.sendMessage(data, { timeout: 10000 })

    if (error) {
      this.removeMessage(newMessage)

      const codes = {
        recurringRequired: () => {
          actions.user.markUserAsSecondBlocked()
          actions.user.markUserAsBlocked('blockRebill')
          this.userInteracted()
        },
        conversationLimitReached: () => {
          actions.user.markUserAsSecondBlocked()
          actions.user.markUserAsBlocked('conversationLimitReached')
        },
        tooManyConversations: () => {
          this.userInteracted()
          onMessageSend({ message, from: data.senderUid, to: data.receiverUid, showPurchaseModal: true })
        },
      }
      if (error.code && codes[error.code]) {
        actions.chat.updateConversationCache(this)
        actions.user.updateUserCache()
        return codes[error.code]()
      }
      if (error.statusCode == 402) {
        if (CONFIG.features.isCreditSite) {
          onMessageSend({ message, from: data.senderUid, to: data.receiverUid, showPurchaseModal: true })
        }
      }
      this.addMessage({...newMessage, id: generateNotSentId()})
      return
    }
    switch (response.type) {
      case MESSAGE_TYPE_UPGRADE:
        this.removeMessage(newMessage)
        if (!response.customConversationBlock) {
          actions.user.markUserAsBlocked()
          actions.user.markUserAsSecondBlocked()
          this.isBlocked = true;
        }
        this.markConversationAsBlocked()
        actions.user.markUserAsFree()
        actions.user.updateUserCache()
        this.userInteracted()
        onMessageSend({ message, from: data.senderUid, to: data.receiverUid, showPurchaseModal: true })
        break
      case MESSAGE_TYPE_SEXICONS_UPGRADE:
        this.removeMessage(newMessage)
        let isCreditSite = true
        if (!CONFIG.features.isCreditSite) {
          isCreditSite = false
        }
        this.addTempMessage({
          hideOnClick: ['.chat-item-picker-toggle', '.upload-button-container', '.chat-input', '.chat-input-button', '.sex-icon-wrapper'],
          title: 'warning',
          message: isCreditSite ? 'messages.upgradeSexiconsCredits' : 'messages.upgradeSexicons',
        })
        if (CONFIG.features.isCreditSite) {
          onMessageSend({ message, from: data.senderUid, to: data.receiverUid, showPurchaseModal: true })
        }
        break
      case MESSAGE_TYPE_CONTACT_INFO:
        this.removeMessage(newMessage)
        this.addMessage(response)
        break
      case MESSAGE_TYPE_FEATURED_AP_UNLOCK:
        this.removeMessage(newMessage)
        this.featuredApUnlock.toggleChatLocked(true);
        break
      default:
        //increase message counters
        this.meMessageCount++

        newMessage.id = response.id
        newMessage.sentAt = response.sentAt
        this.lastMessageSentAt = response.sentAt
        break
    }
  }
  async uploadImage(file) {
    const { onMessageSend } = CONFIG.hooks
    const pendingImage = await this.addImage({
      message: URL.createObjectURL(file),
      id: PENDING_MESSAGE_ID,
      sentAt: Math.floor(Date.now()/1000),
      sentBy: this.meIndex,
      type: MESSAGE_TYPE_IMAGE,
    })

    setTimeout(() => {
      if (this.messages.find(m => m.id === PENDING_MESSAGE_ID && m.type === MESSAGE_TYPE_IMAGE)) {
        this.addImage({...pendingImage, id: generateNotSentId()})
      }
    }, 10000)

    const mimeType = file.type
    const [imageUploaded, imageUploadError] = await ccpProvider.api.ccp.uploadImage({ conversationUid: this.conversationUid, file, mimeType })
    if (imageUploadError) {
      this.removeMessage(pendingImage)
      this.addImage({...pendingImage, id: generateNotSentId()})
      actions.chat.updateConversationCache(this)
      if (CONFIG.features.isCreditSite) {
        onMessageSend({ showPurchaseModal: true })
      }
      return [null, imageUploadError]
    }
    return [imageUploaded]
  }

  @action
  async sendTypingIndicator(characterCount) {
    const params = {
      senderUid: this.meUid,
      receiverUid: this.otherUserUid,
      conversationUid: this.conversationUid,
      characterCount,
    }
    await ccpProvider.api.ccp.typing(params)
  }

  @action
  addMessage(data) {
    if (data.id === -1) {
      data.id = --this.interfaceNotificationIndex
    }
    const exists = this.messages.some(m => m.id === data.id)
    if (!exists) {
      if (CONFIG.userUid === data.receiverUid || data.type === MESSAGE_TYPE_CONTACT_INFO || data.type === MESSAGE_TYPE_VOICE) {
        if (data.type === MESSAGE_TYPE_DEFAULT || data.type === MESSAGE_TYPE_VOICE) {
          this.messageReceivedAt = Date.now()
        }
        else if (!['unblock', 'block'].includes(data.message) && this.notificationReceivedAt !== data.sentAt && !data.showUntil) {
          const notified = store.conversations.some(c => c.notificationReceivedAt === data.sentAt)
          if (!notified) {
            this.notificationReceivedAt = data.sentAt
          }
        }
      }
      const newMessage = new Message(data)

      //check if there is still a pending message
      const pendingMessage = this.messages.find(m => m.id === PENDING_MESSAGE_ID)
      if (pendingMessage) {
        this.removeMessage(pendingMessage)
      }

      this.messages.push(newMessage)

      const notMine = data.sentBy !== this.meIndex // when is this false if I am the receiver?
      const unread = !this.meReadAt || newMessage.sentAt >= this.meReadAt
      const validTypeForCounter = [MESSAGE_TYPE_DEFAULT, MESSAGE_TYPE_CONTACT_INFO, MESSAGE_TYPE_SEXICON, MESSAGE_TYPE_VOICE].includes(data.type) // not a system message
      if (unread && notMine && validTypeForCounter) {
        this.unreadByMeCount++
      }

      actions.chat.updateConversationCache(this)
      return newMessage
    }
  }

  @action
  async addImage(data) {
    if (data.id === -1) {
      data.id = --this.interfaceNotificationIndex
    }

    const pendingImage = this.messages.find(m => m.id === PENDING_MESSAGE_ID && m.type === MESSAGE_TYPE_IMAGE)
    const exists = this.messages.some(m => m.id === data.id)

    if (exists) {
      return
    }

    if (CONFIG.userUid === data.receiverUid) {
      this.messageReceivedAt = Date.now()
    }

    if (data.conversationUid) {
        try {
          new URL(data.message)
        } catch (err) {
          const imageKey = data.message
          data.message = CONFIG.mediaUrl + CONFIG.identifier + '/' + data.conversationUid.replace(':', '_') + '/' + imageKey
        }
    }

    const newMessage = new Message(data)
    if (pendingImage) {
      this.removeMessage(pendingImage)
    }

    //need to wait a bit till message length changes are full propagated through the components
    return wait(50).then(() => {
      this.messages.push(newMessage)
      const notMine = data.sentBy !== this.meIndex // when is this false if I am the receiver?
      const unread = !this.meReadAt || newMessage.sentAt >= this.meReadAt
      if (unread && notMine) {
        this.unreadByMeCount++
      }

      actions.chat.updateConversationCache(this)
      return newMessage
    })
  }

  @action
  reorder(data) {
    const exists = this.messages.some(m => (m.id === data.id))
    if (!exists || data.type === MESSAGE_TYPE_UPGRADE) {
      this.addMessage(data)
    }
    else {
      this.messages.filter(m => m.id === data.id ).forEach(m => {
        m.sentAt = data.sentAt
      })
      this.messages = uniqBy(orderBy(this.messages, ['sentAt', 'id'], ['asc', 'asc']), 'id')
    }
  }

  @action
  removeMessage(data) {
    const messageToRemove = new Message(data)
    this.messages.replace(this.messages.filter(m => !isEqual(messageToRemove, m)))
  }

  readConversation() {
    actions.chat.readConversation(this)
  }

  setLocations() {
    const p = this.otherUserProfile
    if (!isEmpty(p)) {
      p.location = [p.city, p.state, p.country].filter(e => e).join(', ') || CONFIG.translations.basic.noLocation
    }
  }

  userInteracted() {
    if (CONFIG.features.blockAfterInteraction) {
      const conversations = storage.get(`ccp-${CONFIG.userUid}-interacted`, [])
      if (!conversations.includes(this.conversationUid)) {
        storage.set(`ccp-${CONFIG.userUid}-interacted`, [...conversations, this.conversationUid])
      }
    }
  }

  markConversationAsBlocked() {
    const conversations = storage.get(`ccp-${CONFIG.userUid}-blocked-conversations`, [])
    if (!conversations.includes(this.conversationUid)) {
      storage.set(`ccp-${CONFIG.userUid}-blocked-conversations`, [...conversations, this.conversationUid])
    }
  }

  @action
  addTempMessage(tempMessage) {
    this.tempMessages = uniqBy([ ...toJS(this.tempMessages), tempMessage ], 'message')
  }
  @action
  removeTempMessage(message) {
    this.tempMessages = message ? this.tempMessages.filter(x => x.message !== message) : []
  }

  setLocation(location) {
    this.otherUserProfile = {...this.otherUserProfile, location, showDistanceInsteadOfLocation: false}
  }

  @computed get location() {
    return this.otherUserProfile.location;
  }

  @action
  async unlockFeaturedAp() {
    this.featuredApUnlock.isLoading = true;
    try {
      const [response, error] = await ccpProvider.api.user.unlockFeaturedAp({ conversationUid: this.conversationUid, apUid: this.otherUserUid })
      if (error) {
          if (error.code === 'InsufficientBalance') {
            CONFIG.hooks?.onMessageSend?.({ showPurchaseModal: true })
            return
          }
          return
        }
      if (response.success) {
        this.featuredApUnlock.unlockChatWithCredits()
      }
    }
    catch (error) {
      return
    } finally {
      this.featuredApUnlock.isLoading = false;
    }
  }
}
